import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pencil, Trash2, ArrowRightLeft, User, HelpCircle, Plus, AlertCircle } from 'lucide-react';
import { usePenNames, type PenName } from '../hooks/usePenNames';
import { useAuth } from '../hooks/useAuth';
import CreatePenNameForm from './CreatePenNameForm';
import { supabase } from '../lib/supabase';

export default function PenNameManager() {
  const [searchParams] = useSearchParams();
  const { penNames, loading, error, createPenName, deletePenName, switchToPenName } = usePenNames();
  const { user } = useAuth();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(searchParams.get('showPenNameForm') === 'true');
  const [userRole, setUserRole] = useState<'reader' | 'author' | 'admin' | null>(null);

  const currentIdentityId = localStorage.getItem('current_identity_id');

  // Fetch user role on component mount
  React.useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) return;
      
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        setUserRole(profile?.role || null);
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    fetchUserRole();
  }, [user]);

  const handleCreate = async (penNameData: {
    username: string;
    full_name: string;
    bio: string;
    website: string;
    social_links: Record<string, string>;
    avatar_url?: string;
  }) => {
    try {
      if (userRole === 'reader') {
        setFormError('You need to upgrade to an author account to create pen names.');
        return;
      }

      if (penNames.length >= 5) {
        setFormError('You can only have up to 5 pen names. Please delete an existing pen name first.');
        return;
      }
      await createPenName(penNameData);
      setFormError(null);
      setShowForm(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create pen name');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this pen name?')) return;
    await deletePenName(id);
  };

  const handleSwitch = async (id: string) => {
    try {
      if (id === user?.id) {
        localStorage.removeItem('current_identity_id');
      } else {
        await switchToPenName(id);
      }
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch identity:', err);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${userRole === 'reader' ? 'opacity-50' : ''}`}>
      {/* Help Section */}
      {showHelp && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-blue-900 mb-2">Using Pen Names</h3>
              <ul className="text-blue-800 space-y-2">
                <li>• You can create up to 5 pen names to write under different identities</li>
                <li>• Switch between identities using the switch icon next to each pen name.</li>
                <li>• Each pen name has its own profile, followers, and book ideas</li>
                <li>• Your main profile remains unchanged and you can always switch back</li>
              </ul>
              <button 
                onClick={() => setShowHelp(false)}
                className="text-blue-600 hover:text-blue-800 text-sm mt-4"
              >
                Got it, don't show this again
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {formError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {formError}
        </div>
      )}

      {userRole === 'reader' && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
            <div>
              <h3 className="font-medium text-yellow-900">Author Account Required</h3>
              <p className="text-yellow-700 text-sm mt-1">
                You need to upgrade to an author account to create and manage pen names. 
                Visit your settings page to upgrade your account or click upgrade above.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Identities</h2>
          {userRole !== 'reader' && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Pen Name
            </button>
          )}
        </div>

        {showForm && (
          <CreatePenNameForm
            onSubmit={handleCreate}
            onCancel={() => {
              setShowForm(false);
              setFormError(null);
            }}
          />
        )}

        {/* Main Profile */}
        {user && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-100">
                  <User className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{user.email}</h3>
                  <span className="text-sm text-gray-500">Main Profile</span>
                </div>
              </div>
              <button
                onClick={() => handleSwitch(user.id)}
                disabled={!currentIdentityId}
                className={`p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg disabled:opacity-50 ${
                  !currentIdentityId ? 'bg-indigo-50' : ''
                }`}
                title={currentIdentityId ? "Switch to main profile" : "Currently using main profile"}
              >
                <ArrowRightLeft className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Pen Names */}
        {penNames.map((penName) => (
          <div
            key={penName.id}
            className="bg-white rounded-lg shadow-md p-6"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {penName.avatar_url ? (
                  <img
                    src={penName.avatar_url}
                    alt={penName.username}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-100">
                    <User className="w-6 h-6 text-gray-600" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold">{penName.username}</h3>
                  {penName.full_name && (
                    <p className="text-gray-600">{penName.full_name}</p>
                  )}
                  <span className="text-sm text-gray-500">Pen Name</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSwitch(penName.id)}
                  className={`p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg ${
                    currentIdentityId === penName.id ? 'bg-indigo-50' : ''
                  }`}
                  title={currentIdentityId === penName.id ? "Currently using this pen name" : "Switch to this pen name"}
                >
                  <ArrowRightLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setEditingId(penName.id)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(penName.id)}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}